import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { convertMinToHrsMin, localRandomUuid } from 'src/utils/stringHelpers';
import useWebSocket from 'src/utils/useWebSocket';
import ProgressBarLite from 'src/components/ProgressBarDouble';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';

interface ProjectTimeReportCardProps {
  hideSeeDetail?: boolean;
  hideAvailable?: boolean;
  showDescription?: boolean;
  showTooltip?: boolean;
}

const EMPTY_INDICATOR = { value: 0, color: '#e0e0e0', percentage: 0 };

const ProjectTimeReportCard: FC<ProjectTimeReportCardProps> = ({
  hideSeeDetail,
  hideAvailable,
}) => {
  const { budget: initialBudget, uuid: projectUuid } = (useSelector(
    (globalState: RootState) => globalState.projectRetainer.data
  ) ?? {}) as any;

  const [budget, setBudget] = useState(initialBudget);

  const refreshBudget = async () => {
    if (!projectUuid) return;
    try {
      const res = await apiRequests.get(`${apiRoutes.PROJECT_RETAINER}/${projectUuid}`);
      setBudget(res.data.data.budget);
    } catch (_) {}
  };

  useEffect(() => {
    refreshBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid]);

  useWebSocket({
    channelName: 'model.changes',
    listen: [
      {
        event: '.app.models.projects.request',
        callback: async (event: any) => {
          if (event.payload?.status !== 'draft' && event.payload?.ref_token !== localRandomUuid) {
            await refreshBudget();
          }
        },
      },
      {
        event: '.app.models.projects.projectcredit',
        callback: async () => {
          await refreshBudget();
        },
      },
      {
        event: '.app.models.projects.timeentry',
        callback: async (event: any) => {
          if (
            event.payload?.project_uuid === projectUuid &&
            event.payload?.ref_token !== localRandomUuid
          ) {
            await refreshBudget();
          }
        },
      },
    ],
  });

  return (
    <div className="w-full space-y-1 text-sm text-primary">
      <div className="flex items-center justify-between">
        <span className="font-bold">Hours balance</span>

        {!hideSeeDetail && (
          <Link to="/usage" className="text-xs hover:text-blue-500 xl:hidden">
            View details
          </Link>
        )}
      </div>

      {!hideAvailable && (
        <div className="flex items-center justify-between gap-2">
          <span>Available</span>

          <span>
            <span
              className={
                budget?.bar?.available?.value < 0 ? 'text-red-500' : 'text-blue-700'
              }
            >
              {convertMinToHrsMin(budget?.bar?.available?.value)}
            </span>{' '}
            / {convertMinToHrsMin(budget?.total)} h
          </span>
        </div>
      )}

      <ProgressBarLite
        budget={budget?.total ?? 0}
        available={budget?.bar?.available ?? EMPTY_INDICATOR}
        estimated={budget?.bar?.estimated ?? EMPTY_INDICATOR}
        spent={budget?.bar?.spent ?? EMPTY_INDICATOR}
      />

      {!hideSeeDetail && (
        <Link
          to="/usage"
          className="hidden text-xs hover:text-blue-500 xl:block"
        >
          View details
        </Link>
      )}
    </div>
  );
};

export default ProjectTimeReportCard;
